import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Bell, Loader2, MapPin } from "lucide-react";

type PermState = "granted" | "denied" | "prompt" | "prompt-with-rationale" | "unknown";

type PermissionsGateProps = {
  children: React.ReactNode;
  requirePush?: boolean;
  requireGps?: boolean;
  title: string;
  description: string;
};

export function PermissionsGate({
  children,
  requirePush = false,
  requireGps = false,
  title,
  description,
}: PermissionsGateProps) {
  const [native, setNative] = useState(false);
  const [pushPerm, setPushPerm] = useState<PermState>("unknown");
  const [gpsPerm, setGpsPerm] = useState<PermState>("unknown");
  // Whether OS-level Location Services are actually on. Permission can be
  // "granted" while the device-wide toggle is off — checkPermissions() doesn't
  // see that. Verified by attempting getCurrentPosition.
  const [gpsServicesOn, setGpsServicesOn] = useState<boolean | "unknown">("unknown");
  const [checking, setChecking] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [, setLocation] = useLocation();

  const refresh = useCallback(async () => {
    let nextPushPerm: PermState = "unknown";
    let nextGpsPerm: PermState = "unknown";
    let nextGpsServicesOn: boolean | "unknown" = "unknown";
    let nextNative = false;

    try {
      const { Capacitor } = await import("@capacitor/core");
      if (!Capacitor.isNativePlatform()) {
        setNative(false);
        setInitialized(true);
        return;
      }
      nextNative = true;
      if (requirePush) {
        const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
        const result = await FirebaseMessaging.checkPermissions();
        nextPushPerm = (result.receive as PermState) ?? "unknown";
      }
      if (requireGps) {
        const { Geolocation } = await import("@capacitor/geolocation");
        const result = await Geolocation.checkPermissions();
        const coarse = (result.coarseLocation as PermState) ?? "unknown";
        const fine = (result.location as PermState) ?? "unknown";
        nextGpsPerm = fine === "granted" || coarse === "granted" ? "granted" : fine;
        if (nextGpsPerm === "granted") {
          // Permission says granted, but the OS Location Services toggle could
          // still be off. Probe with a short-timeout position request — if it
          // throws, services are off (or signal is genuinely unavailable, but
          // the user-facing fix is the same: enable Location Services).
          // maximumAge MUST be 0: any cached fix from earlier in the session
          // (when services may have been on) will satisfy the request without
          // consulting the current OS Location Services state, defeating the
          // probe.
          // Bumped from 4s → 12s. A cold GPS fix indoors on Android can take
          // 6-10 seconds even with services on; 4s was producing false
          // "services off" rejections that the user couldn't recover from
          // without restarting the app. `maximumAge` MUST stay 0 (trap #14)
          // so we don't accept a fix from before the user toggled services.
          try {
            await Geolocation.getCurrentPosition({
              enableHighAccuracy: false,
              timeout: 12000,
              maximumAge: 0,
            });
            nextGpsServicesOn = true;
          } catch {
            nextGpsServicesOn = false;
          }
        }
      }
    } catch {
      nextNative = false;
    } finally {
      setNative(nextNative);
      setPushPerm(nextPushPerm);
      setGpsPerm(nextGpsPerm);
      setGpsServicesOn(nextGpsServicesOn);
      setInitialized(true);
    }
  }, [requirePush, requireGps]);

  useEffect(() => {
    void refresh();
    let appListener: { remove: () => Promise<void> } | null = null;
    (async () => {
      try {
        const { App } = await import("@capacitor/app");
        const handle = await App.addListener("appStateChange", ({ isActive }) => {
          if (isActive) void refresh();
        });
        appListener = handle;
      } catch {
        /* not native */
      }
    })();
    return () => {
      if (appListener) void appListener.remove();
    };
  }, [refresh]);

  const request = useCallback(async () => {
    setChecking(true);
    try {
      if (requirePush && pushPerm !== "granted" && pushPerm !== "denied") {
        const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
        const result = await FirebaseMessaging.requestPermissions();
        setPushPerm((result.receive as PermState) ?? "unknown");
        if (result.receive === "granted") {
          // Force the FCM token to be minted now so the device gets
          // registered server-side without waiting for the next cold
          // start. The tokenReceived listener in native.ts POSTs it.
          await FirebaseMessaging.getToken().catch(() => undefined);
        }
      }
      if (requireGps && gpsPerm !== "granted" && gpsPerm !== "denied") {
        const { Geolocation } = await import("@capacitor/geolocation");
        const result = await Geolocation.requestPermissions();
        const coarse = (result.coarseLocation as PermState) ?? "unknown";
        const fine = (result.location as PermState) ?? "unknown";
        setGpsPerm(fine === "granted" || coarse === "granted" ? "granted" : fine);
      }
    } catch {
      /* keep UI state */
    } finally {
      setChecking(false);
    }
  }, [requirePush, requireGps, pushPerm, gpsPerm]);

  const pushMissing = requirePush && pushPerm !== "granted";
  const gpsMissing =
    requireGps && (gpsPerm !== "granted" || gpsServicesOn === false);
  const gpsServicesOff = requireGps && gpsPerm === "granted" && gpsServicesOn === false;
  const anyDenied =
    (requirePush && pushPerm === "denied") ||
    (requireGps && gpsPerm === "denied") ||
    gpsServicesOff;

  // Don't flash the gate before the initial permission check populates —
  // "unknown" means we haven't heard back yet.
  const resolved = useMemo(() => {
    if (requirePush && pushPerm === "unknown") return false;
    if (requireGps && gpsPerm === "unknown") return false;
    if (requireGps && gpsPerm === "granted" && gpsServicesOn === "unknown") return false;
    return true;
  }, [requirePush, requireGps, pushPerm, gpsPerm, gpsServicesOn]);

  // Guideline 4.5.4: an app may not require push notifications to function.
  // Orders, chat and delivery updates are all in EMAIL_ALLOWED_TYPES and are
  // always-on operational types, so a seller without push still gets an email
  // and an in-app notification for every order — nothing is actually missed.
  // Blocking on push also strands anyone who taps "Don't Allow" once, since iOS
  // only shows the system prompt a single time and pushPerm stays "denied"
  // forever after. GPS stays a hard gate: a GPS-verified delivery genuinely
  // cannot run without location.
  const [pushSkipped, setPushSkipped] = useState(false);
  const shouldGate =
    native && resolved && (gpsMissing || (pushMissing && !pushSkipped));

  // Before the initial check resolves, render nothing on native so no child
  // side effect (GPS polling, etc.) can fire. On web this pass is immediate.
  if (!initialized) return null;
  if (!shouldGate) return <>{children}</>;

  return (
    <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-background p-6 text-center">
      <button
        type="button"
        onClick={() => setLocation("/")}
        className="absolute left-4 flex items-center gap-1 rounded-full px-2 py-1 text-sm font-medium text-muted-foreground hover:text-foreground"
        style={{ top: "calc(0.75rem + var(--safe-area-top, 0px))" }}
        aria-label="Back to app"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
        {requireGps ? <MapPin className="h-8 w-8 text-primary" /> : <Bell className="h-8 w-8 text-primary" />}
      </div>
      <h1 className="mt-5 text-2xl font-semibold">{title}</h1>
      <p className="mt-3 max-w-sm text-sm text-muted-foreground">
        {description}
        {anyDenied
          ? " One or more permissions are blocked. Enable them in your device settings and return here."
          : " Tap the button below to grant them."}
      </p>

      <ul className="mt-4 space-y-1 text-sm">
        {requirePush && (
          <li className={pushPerm === "granted" ? "text-brand-secondary" : "text-destructive"}>
            Notifications: {pushPerm === "granted" ? "on" : pushPerm === "denied" ? "blocked" : "off"}
          </li>
        )}
        {requireGps && (
          <li
            className={
              gpsPerm === "granted" && gpsServicesOn === true
                ? "text-brand-secondary"
                : "text-destructive"
            }
          >
            Location:{" "}
            {gpsServicesOff
              ? "device location services are off"
              : gpsPerm === "granted"
                ? "on"
                : gpsPerm === "denied"
                  ? "blocked"
                  : "off"}
          </li>
        )}
      </ul>

      <div className="mt-6 flex w-full max-w-xs flex-col gap-3">
        {!anyDenied && (
          <Button onClick={request} disabled={checking} size="lg">
            {checking ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Requesting…
              </>
            ) : (
              "Grant permissions"
            )}
          </Button>
        )}
        <Button variant="outline" onClick={refresh} size="lg" disabled={checking}>
          {anyDenied ? "I've enabled them — check again" : "Check again"}
        </Button>
        {pushMissing && !gpsMissing && (
          <Button variant="ghost" onClick={() => setPushSkipped(true)} size="lg">
            Continue without notifications
          </Button>
        )}
        <Button variant="ghost" onClick={() => setLocation("/")} size="lg">
          Back to home
        </Button>
      </div>

      {anyDenied && (
        <div className="mt-6 max-w-sm rounded-lg border bg-muted/30 p-3 text-left text-xs text-muted-foreground space-y-2">
          {gpsServicesOff && (
            <div>
              <div className="font-semibold text-foreground">Turn on Location Services</div>
              <div>iOS: Settings → Privacy &amp; Security → Location Services.</div>
              <div>Android: Settings → Location → toggle on.</div>
            </div>
          )}
          {((requirePush && pushPerm === "denied") || (requireGps && gpsPerm === "denied")) && (
            <div>
              <div className="font-semibold text-foreground">Enable app permissions</div>
              <div>Settings → Apps → Stoop → Permissions / Notifications.</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
